/**
 * @file Components/Sections/Destinations.jsx
 * @description Destinations showcase section. Displays popular travel
 * destinations in a visual grid with images and quick info.
 */

import React from 'react';
import PropTypes from 'prop-types';

const Destinations = ({ destinations = [], title = "Popular Destinations" }) => {
  if (!destinations || destinations.length === 0) {
    return (
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              {title}
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              No destinations available at the moment.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            {title}
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Explore amazing destinations around the world
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {(destinations || []).map((destination, index) => (
            <div
              key={index}
              className="relative group cursor-pointer overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="aspect-w-16 aspect-h-12 h-64">
                <img
                  src={destination.image}
                  alt={destination.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-xl font-bold mb-2">
                  {destination.name}
                </h3>
                
                {destination.country && (
                  <p className="text-sm text-gray-200 mb-2">
                    {destination.country}
                  </p>
                )}
                
                {destination.description && (
                  <p className="text-sm text-gray-300 mb-3 line-clamp-2">
                    {destination.description}
                  </p>
                )}

                <div className="flex items-center justify-between">
                  {destination.tourCount && (
                    <span className="text-sm text-gray-300">
                      {destination.tourCount} tours
                    </span>
                  )}
                  
                  {destination.startingPrice && (
                    <span className="text-sm font-semibold">
                      From ₹{destination.startingPrice}
                    </span>
                  )}
                </div>
              </div>

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-blue-600 bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button className="bg-white text-blue-600 px-6 py-2 rounded-full font-medium hover:bg-gray-100 transition-colors duration-200">
                    Explore
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <button className="bg-blue-600 text-white px-8 py-3 rounded-md hover:bg-blue-700 transition-colors duration-200">
            View All Destinations
          </button>
        </div>
      </div>
    </div>
  );
};

Destinations.propTypes = {
  destinations: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      country: PropTypes.string,
      image: PropTypes.string.isRequired,
      description: PropTypes.string,
      tourCount: PropTypes.number,
      startingPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  title: PropTypes.string
};

export default Destinations;
